// Frontend mirror of apps/api/src/types.ts. Keep in sync — these are the
// shapes the API returns.

export type Severity = 'low' | 'medium' | 'high' | 'critical';
export type ModuleId =
  | 'dns' | 'email' | 'web' | 'ssl' | 'whois' | 'subdomains' | 'techstack' | 'infra';

export interface Alert {
  severity: Severity;
  module: ModuleId;
  title: string;
  description?: string;
}
export interface Recommendation {
  priority: 'low' | 'medium' | 'high' | 'strategic';
  module: ModuleId;
  text: string;
}

export interface DnsRecords {
  a: string[]; aaaa: string[];
  mx: { preference: number; exchange: string }[];
  ns: string[]; txt: string[];
  caa: { flags: number; tag: string; value: string }[];
  soa?: {
    mname: string; rname: string; serial: number;
    refresh: number; retry: number; expire: number; minimum: number;
  };
}
export interface DnssecResult {
  enabled: boolean; ds_records: number;
  validation_chain: number; validation_steps: number;
  algorithm?: string; key_tag?: string;
  details?: Record<string, unknown>;
}
export interface NameserverAnalysis {
  count: number; providers: string[]; single_operator: boolean;
}
export interface DnsModule {
  records: DnsRecords; dnssec: DnssecResult;
  caa_count: number; ip_count: number; mx_count: number;
  nameservers?: NameserverAnalysis;
  score: number;
}

export interface SpfResult {
  configured: boolean; record?: string;
  qualifier?: 'pass' | 'soft_fail' | 'hard_fail' | 'neutral';
  authorized_senders: string[]; rfc7208_compliant: boolean;
  dns_lookups?: number; void_lookups?: number;
  evaluation?: 'ok' | 'permerror' | 'temperror';
  permerror_reason?: string;
}
export interface DkimResult {
  configured: boolean;
  selectors: {
    selector: string; record: string; algorithm?: string; key_size?: number;
    key_type?: 'rsa' | 'ed25519' | 'unknown'; testing?: boolean; revoked?: boolean;
  }[];
}
export interface DmarcResult {
  configured: boolean; record?: string;
  policy?: 'none' | 'quarantine' | 'reject';
  pct?: number; rua?: string[]; ruf?: string[];
  alignment_spf?: 'r' | 's'; alignment_dkim?: 'r' | 's';
  platform?: { name: string; confidence: number; description: string;
    kind?: 'vendor' | 'hosting-default' };
}
export interface BimiResult {
  configured: boolean; record?: string; logo_url?: string; vmc_url?: string;
}
export interface MtaStsResult {
  configured: boolean; record?: string; policy_id?: string;
  mode?: 'enforce' | 'testing' | 'none'; mx_patterns?: string[];
  max_age?: number; mx_covered?: boolean; issues?: string[];
}
export interface TlsRptResult {
  configured: boolean; record?: string; rua?: string[];
}
export type MicrosoftCloud = 'commercial' | 'gcc-high' | 'dod' | 'china' | 'other';
export interface MicrosoftFederation {
  provider: string;
  cloud: MicrosoftCloud;
  sovereign: boolean;
  is_microsoft: boolean;
  label: string;
}

export interface EmailModule {
  spf: SpfResult; dkim: DkimResult; dmarc: DmarcResult;
  bimi: BimiResult; mta_sts: MtaStsResult; tls_rpt: TlsRptResult;
  microsoft?: MicrosoftFederation;
  authentication_score: number;
  grade: 'A' | 'B' | 'C' | 'D' | 'F';
  score: number;
}

export interface WebHeaders {
  hsts?: string; csp?: string; csp_report_only?: boolean;
  x_frame_options?: string;
  x_xss_protection?: string; x_content_type_options?: string;
  referrer_policy?: string; permissions_policy?: string; server?: string;
}
export interface WebModule {
  url: string; status?: number; redirected_to?: string;
  headers: WebHeaders; missing: string[]; score: number;
}

export interface SslModule {
  hostname: string; reachable: boolean; cert_count: number;
  recent_cert?: {
    issuer_name: string; common_name: string;
    not_before: string; not_after: string; serial_number: string;
  };
  days_to_expiry?: number; score: number;
}

export interface WhoisModule {
  found: boolean;
  rate_limited?: boolean;
  registry?: string;
  registrar?: string;
  registered?: string; expires?: string; updated?: string;
  age_days?: number;
  nameservers: string[]; status: string[]; raw_url?: string; score: number;
}

export interface SubdomainTakeover {
  host: string; cname: string; service?: string;
  reason: 'dangling-cname' | 'vulnerable-service';
  confidence: 'low' | 'medium';
}
export interface SubdomainsModule {
  count: number; hosts: string[]; source: 'crt.sh';
  wildcards?: string[];
  wildcard_dns?: boolean;
  notable?: { host: string; category: string }[];
  takeover_candidates?: SubdomainTakeover[];
}

export interface SecurityTxtResult {
  present: boolean; valid: boolean; contacts: string[];
  expires?: string; expired?: boolean;
  has_policy: boolean; has_encryption: boolean; has_canonical: boolean;
  issues: string[];
}
export interface TechModule {
  technologies: { name: string; category: string; confidence: number; version?: string }[];
  external_links?: string[];
  security_txt?: SecurityTxtResult;
  error?: string;
}

// ---------- Infrastructure / ASN ----------
export interface InfraHost {
  ip: string; version: 4 | 6; ptr?: string;
  asn?: number; as_name?: string; prefix?: string;
  country?: string; registry?: string; provider?: string;
}
export interface InfraModule {
  hosts: InfraHost[];
  asns: { asn: number; name?: string; country?: string }[];
  providers: string[];
  cdn_fronted: boolean;
  score: number;
}

// ---------- Vendor surface ----------
export type VendorCategory =
  | 'Email' | 'Productivity' | 'Communication' | 'Storage'
  | 'Identity' | 'CRM' | 'Developer' | 'Marketing' | 'Payments'
  | 'Security' | 'Other';
export type VendorSource = 'mx' | 'txt' | 'link' | 'subdomain';
export interface VendorMatch {
  name: string;
  category: VendorCategory;
  evidence: string;
  source: VendorSource;
}
export interface VendorsModule {
  email_platform?: VendorMatch;
  vendors: VendorMatch[];
  total: number;
}

export type VulnSeverity = 'low' | 'medium' | 'high' | 'critical';
export interface VulnFinding {
  package_name: string;
  package_version: string;
  cve_id: string;
  summary: string;
  severity?: VulnSeverity;
  url: string;
  published?: string;
}
export interface VulnsModule {
  source: 'osv.dev';
  queries_made: number;
  unmapped: string[];
  vulnerabilities: VulnFinding[];
}

export type ExposureSeverity = 'critical' | 'high' | 'medium' | 'low' | 'info';
export type ExposureStatus = 'exposed' | 'safe' | 'unknown';
export interface ExposureFinding {
  id: string;
  category: 'Source-Code Exposure' | 'Admin Surface' | 'Information Disclosure' | 'Transport' | 'CORS';
  title: string;
  description: string;
  status: ExposureStatus;
  severity: ExposureSeverity;
  evidence?: string;
  url?: string;
}
export interface ExposureModule {
  checked: number;
  exposed: number;
  findings: ExposureFinding[];
}

// ---------- Compliance ----------
export type ControlStatus = 'satisfied' | 'partial' | 'gap' | 'unknown';
export type ControlRelatedModule =
  | 'dns' | 'email' | 'web' | 'ssl' | 'whois' | 'subdomains'
  | 'techstack' | 'vulns' | 'exposure' | 'infra';
export interface ControlEvidence {
  control_id: string;
  control_name: string;
  status: ControlStatus;
  evidence: string[];
  related_module?: ControlRelatedModule;
}
export interface ControlFamily {
  name: string;
  controls: ControlEvidence[];
}
export type FrameworkId = 'cmmc' | 'soc2' | 'iso27001' | 'nist_csf' | 'pci_dss';
export interface FrameworkReport {
  id: FrameworkId;
  name: string;
  description: string;
  satisfied: number;
  partial: number;
  gap: number;
  unknown: number;
  total: number;
  families: ControlFamily[];
}
export interface ComplianceReport {
  frameworks: FrameworkReport[];
}

export interface PriorityFix {
  id: string;
  title: string;
  rationale: string;
  effort: 'low' | 'medium' | 'high';
  impact: 'security' | 'deliverability' | 'compliance' | 'brand' | 'availability';
  module: string;
  frameworks: string[];
}
export interface ExecutiveSummary {
  headline: string;
  narrative: string;
  posture: 'excellent' | 'good' | 'fair' | 'poor';
  top_fixes: PriorityFix[];
}

// ---------- Live scan stream (NDJSON) ----------
export interface ScanModuleMeta {
  id: string;
  label: string;
}

export const SCAN_MODULES: ScanModuleMeta[] = [
  { id: 'dns', label: 'DNS & registry' },
  { id: 'email', label: 'Email authentication' },
  { id: 'web', label: 'Web & transport' },
  { id: 'ssl', label: 'TLS certificates' },
  { id: 'whois', label: 'WHOIS / RDAP' },
  { id: 'infra', label: 'Infrastructure & ASN' },
  { id: 'subdomains', label: 'Subdomain discovery' },
  { id: 'techstack', label: 'Tech fingerprint' },
  { id: 'vulns', label: 'CVE matching' },
  { id: 'exposure', label: 'Exposure probes' }
];

export type ScanModuleStatus = 'start' | 'ok' | 'fail';

export type ScanEvent =
  | { type: 'start'; id: string; domain: string; modules: ScanModuleMeta[] }
  | {
      type: 'module';
      id: string;
      label: string;
      status: ScanModuleStatus;
      ms?: number;
      brief?: string;
    }
  | { type: 'result'; result: ScanResult }
  | { type: 'error'; message: string };

// ---------- Remediation ----------
export interface RemediationRecord {
  type: 'TXT' | 'CAA' | 'CNAME' | 'MX' | 'file';
  host: string;
  value: string;
}
export interface RemediationSnippet {
  label: string;
  code: string;
}
export interface Remediation {
  id: string;
  module: string;
  title: string;
  summary: string;
  steps: string[];
  records?: RemediationRecord[];
  snippets?: RemediationSnippet[];
  effort: 'low' | 'medium' | 'high';
  impact: 'security' | 'deliverability' | 'compliance' | 'brand' | 'availability';
  refs?: { label: string; url: string }[];
}

export interface ScanResult {
  id: string; domain: string;
  created_at: number; finished_at?: number;
  status: 'pending' | 'running' | 'done' | 'error';
  overall_score: number;
  modules: {
    dns: DnsModule | null;
    email: EmailModule | null;
    web: WebModule | null;
    ssl: SslModule | null;
    whois: WhoisModule | null;
    subdomains: SubdomainsModule | null;
    techstack: TechModule | null;
    vulns: VulnsModule | null;
    exposure: ExposureModule | null;
    infra: InfraModule | null;
    vendors: VendorsModule | null;
  };
  alerts: Alert[];
  recommendations: Recommendation[];
  remediations: Remediation[];
  executive: ExecutiveSummary;
  compliance: ComplianceReport;
}
